import { namespace, extend } from 'wsm-tagging-utils';
import userShouldBeTracked from '../../../../userShouldBeTracked';
import nrSwitcherOn from '../../../../nrSwitcher';

const runAbstractEventHandler = (win) => {
	namespace(win, 'DDC.tracking.shift.handlers');

	const {
		DDC,
		DDC: { tracking },
		newrelic
	} = win;

	/**
	 * The standard EventHandler implementation
	 *
	 * @param {Object} window an instance of the global window object
	 * @param {tracking.framework.runtime.Page} page A Page instance for page related queries
	 * @constructor
	 */
	tracking.shift.handlers.AbstractEventHandler =
		function AbstractEventHandler(thisWin, page, form) {
			tracking.framework.runtime.EventHandler.call(
				this,
				thisWin,
				page,
				form
			);
			this.scriptLocation = undefined;
			this.scriptObjectName = undefined;
			this.clientId = undefined;
			this.dealerCodeType = undefined;
			this.retailerId = undefined;
			this.providerId = undefined;
			this.dealerAreaCode = undefined;
			this.accelerateInitialClick = true;
			this.myWalletInteracted = false;
			// DDC Form Tracking ID to Shift Form Type
			if (win?.DDC?.WidgetData?.['ws-tagging']?.mapping?.shift) {
				this.formTrackingIdMapping =
					win.DDC.WidgetData['ws-tagging'].mapping.shift.form || {};
			}

			// DDC Inventory Type to Shift status mapping
			this.inventoryTypeMapping = {
				NEW: 'New',
				USED: 'Used',
				CERTIFIED: 'CPO',
				ALL: 'Mixed',
				DEFAULT: ''
			};
			// DDC Page Alias to client page name mapping

			// DDC dealerCodeType to Shift pageBrand mapping
			this.pageBrandMapping = {
				CLLC: 'CDJR',
				GENESISDWCP: 'Genesis',
				JAG: 'Jaguar',
				KIA: 'Kia',
				LR: 'Land Rover',
				MAZDA: 'Mazda',
				MBZ: 'Mercedes',
				AR: 'AlfaRomeo',
				FIAT: 'Fiat',
				PORSCHE: 'Porsche',
				TOYOTA: 'Toyota',
				LEXUS: 'Lexus',
				SUBARU: 'Subaru',
				SOA: 'Subaru',
				MASERATI: 'Maserati',
				BMW: 'BMW',
				VW: 'VW'
			};
			// Mapped name of current page
			this.pageName = undefined;

			// DDC Inventory Type to Shift status mapping
			this.fuelTypeMapping = {
				GASOLINE: 'Gasoline',
				DIESEL: 'Diesel',
				ELECTRIC: 'Electric',
				HYBRID: 'Hybrid',
				DEFAULT: 'Gasoline'
			};

			this.isFeaturedVehiclePage =
				this?.page?.widgetIsPresent?.(
					/v9.widgets.inventory-featured.default.v1/
				) &&
				!(
					this?.page
						?.currentPageAlias?.()
						?.includes?.('PROMOTIONS_LISTING') ||
					this?.page
						?.currentPageAlias?.()
						?.includes?.('NO_RESULTS') ||
					this?.page?.currentPageAlias?.() === 'INDEX'
				);
		};

	// Make AbstractEventHandler inherit from EventHandler
	extend(
		tracking.shift.handlers.AbstractEventHandler,
		tracking.framework.runtime.EventHandler
	);

	tracking.shift.handlers.AbstractEventHandler.prototype.logCategory =
		'ws-tagging:AbstractEventHandler';

	// ======================================================================
	// --------------------- Configurable Properties ------------------------
	// ======================================================================

	tracking.shift.handlers.AbstractEventHandler.prototype.getScriptLocation =
		function getScriptLocation() {
			return this.scriptLocation;
		};

	/**
	 * Sets the location of the Shift-provided script that will be injected into the
	 * current page.
	 *
	 * @param {string} scriptLocation The location of the Shift-provided script that will be
	 *                            injected into the current page
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.setScriptLocation =
		function setScriptLocation(scriptLocation) {
			this.scriptLocation = scriptLocation;
		};

	tracking.shift.handlers.AbstractEventHandler.prototype.getScriptObjectName =
		function getScriptObjectName() {
			return this.scriptObjectName;
		};

	/**
	 * Sets the name of the provided script that will be injected into the current page.
	 *
	 * @param {string} scriptObjectName The name of the provided script that will be
	 *                          injected into the current page
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.setScriptObjectName =
		function setScriptObjectName(scriptObjectName) {
			this.scriptObjectName = scriptObjectName;
		};

	tracking.shift.handlers.AbstractEventHandler.prototype.getClientId =
		function getClientId() {
			return this.clientId;
		};

	/**
	 * Sets the client identifier that is sent to Shift as part of the create event.
	 *
	 * @param {string} clientId The client identifier that is sent to Shift as part of the
	 *                    create event.
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.setClientId =
		function setClientId(clientId) {
			this.clientId = clientId;
		};

	tracking.shift.handlers.AbstractEventHandler.prototype.getDealerCodeType =
		function getDealerCodeType() {
			return this.dealerCodeType;
		};

	/**
	 * Sets the DDC dealer code type used to determine Shift page brands.
	 *
	 * @param {string} dealerCodeType The DDC dealer code type.
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.setDealerCodeType =
		function setDealerCodeType(dealerCodeType) {
			this.dealerCodeType = dealerCodeType;
		};

	tracking.shift.handlers.AbstractEventHandler.prototype.getRetailerId =
		function getRetailerId() {
			return this.retailerId;
		};

	/**
	 * Sets the retailer identifier that is sent to Shift as part of the create event.
	 *
	 * @param {string} retailerId The retailer identifier that is sent to Shift as part of the
	 *                    create event.
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.setRetailerId =
		function setRetailerId(retailerId) {
			this.retailerId = retailerId;
		};

	tracking.shift.handlers.AbstractEventHandler.prototype.getProviderId =
		function getProviderId() {
			return this.providerId;
		};

	/**
	 * Sets the provider identifier that is sent to Shift as part of the create event.
	 *
	 * @param {string} providerId The provider identifier that is sent to Shift as part of the
	 *                    create event.
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.setProviderId =
		function setProviderId(providerId) {
			this.providerId = providerId;
		};

	// ======================================================================
	// ------------------------ Lifecycle Operations ------------------------
	// ======================================================================

	/**
	 * Initializes the AbstractEventHandler by injecting the configured
	 * tag container script into the DOM then sending the create event
	 * to Shift.
	 *
	 * This method is guaranteed to be called only after the configurable
	 * properties have been specified.
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.initialize =
		function initializeAbstractEventHandler() {
			this.log.info('initializing...');

			this.log.info(
				`sending create event for clientId: ${this.clientId}, retailerId: ${this.retailerId}, providerId: ${this.providerId}`
			);

			this.win[this.scriptObjectName](
				'create',
				this.clientId,
				this.retailerId,
				this.providerId
			);

			this.win?.CAIEventBus?.on('wallet:updated', (walletData) => {
				if (!this.myWalletInteracted) {
					this.handleWalletUpdated(walletData);
					this.myWalletInteracted = true;
				}
			});

			if (this.saveShiftSessionId) {
				this.win[this.scriptObjectName]('getSessionId', (sessionId) => {
					this.win.sessionStorage.setItem(
						'SaveShiftSession',
						sessionId
					);
				});
			}

			this.log.info('initialized');
		};

	// ======================================================================
	// --------------------------- Utilities --------------------------------
	// ======================================================================

	/**
	 * Returns the form type to send to Shift given the DDC form tracking
	 * identifier.
	 *
	 * @param {string} trackingId The DDC form tracking identifier
	 * @returns {string} The form type to send to Shift
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.mapFormTrackingId =
		function mapFormTrackingId(trackingId) {
			const newTrackingId = trackingId && trackingId.toUpperCase();
			return (
				this.formTrackingIdMapping[newTrackingId] ||
				this.formTrackingIdMapping.DEFAULT
			);
		};

	/**
	 * Returns the inventory type to send to Shift given the DDC compositeType
	 * query facet value.
	 *
	 * @param {string} compositeType The DDC compositeType query facet value
	 * @returns {string} The inventory type to send to Shift
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.mapInventoryType =
		function mapInventoryType(compositeType, newOrUsed, certified) {
			const compositeTypeUppercase = compositeType
				? compositeType?.toUpperCase?.()
				: 'DEFAULT';
			let inventoryType =
				this.inventoryTypeMapping[compositeTypeUppercase];
			if (inventoryType === undefined) {
				if (
					newOrUsed === 'used' &&
					(certified === 'true' || certified === true)
				) {
					inventoryType = this.inventoryTypeMapping.CERTIFIED; // Used Certified
				} else {
					inventoryType =
						this.inventoryTypeMapping[
							(newOrUsed || 'DEFAULT').toUpperCase()
						]; // New or Used
				}
			}

			return inventoryType;
		};

	/**
	 * Returns the page name to send to Shift given the DDC page alias.
	 * Mapping keys are treated as regular expressions used to test
	 * the current page alias for a match.
	 *
	 * If a page has a pageType page property set, that will be used instead.
	 *
	 * @param {string} pageAlias The DDC page alias
	 * @returns {string} The page name to send to Shift
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.mapPageAlias =
		function mapPageAlias() {
			return (
				DDC?.dataLayer?.page?.trackingTags?.pageType ||
				win?.DDC?.WidgetData?.['ws-tagging']?.mapping?.shift?.pagealias
					?.pageType ||
				(this.hasInventoryWidget() && 'Vehicle Listing') ||
				// TODO: Remove this temporary fix when it is no longer temporary: US793715 Not sure when that will be.
				(DDC?.dataLayer?.site?.franchises?.includes?.('subaru') &&
					DDC?.dataLayer?.page?.pageInfo?.pagePath ===
						'/financing/index.htm' &&
					'Finance') ||
				'Other'
			);
		};

	/**
	 * Maps the pageBrand required by Shift from the DDC dealerCodeType.
	 *
	 * @returns {string} pageBrand The page brand to send to Shift.
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.mapPageBrand =
		function mapPageBrand() {
			const { dealerCodeType } = this;
			let pageBrand;

			if (
				(dealerCodeType !== '' || dealerCodeType !== undefined) &&
				typeof dealerCodeType === 'string'
			) {
				if (
					DDC?.dataLayer?.site?.franchises &&
					dealerCodeType.toLowerCase() === 'gmus'
				) {
					pageBrand = '';
					const sortedFranchises =
						DDC.dataLayer.site.franchises.sort();
					sortedFranchises.forEach((val, index, arr) => {
						if (index === arr.length - 1) {
							pageBrand += val.substring(0, 2);
						} else {
							pageBrand += `${val.substring(0, 2)}|`;
						}
					});
				} else {
					pageBrand =
						this.pageBrandMapping[dealerCodeType.toUpperCase()] ||
						'';
				}
			}

			return pageBrand;
		};

	// ======================================================================
	// ------------------------- Page Data Building -------------------------
	// ======================================================================

	/**
	 * Builds the data object to send Shift on a page view event.
	 *
	 * @param {string} alias the DDC page alias of the page being viewed
	 * @returns {Object} The shift data object to send
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.buildDataForPageView =
		function buildDataForPageView() {
			return {};
		};

	// ======================================================================
	// -------------------------- Send Events to Shift ----------------------
	// ======================================================================

	/**
	 * Sends the specified data to Shift.
	 *
	 * @param {Object} data An object containing the data to send to Shift
	 * @param {string} event An optional event name, may be undefined
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.sendData =
		function sendData(data, event) {
			const immutabledata = JSON.parse(JSON.stringify(data));
			if (newrelic) {
				const pageActionAttributes = {
					wsTaggingGrouping: this?.form?.wsTaggingGrouping || '',
					useFormAPI: true // always true via useFormAPI: true
				};
				const eventName = (data && data.events) || event;
				if (eventName) {
					pageActionAttributes.eventName = eventName;
				}
				if (
					userShouldBeTracked() &&
					nrSwitcherOn(
						this.flags,
						this.project,
						pageActionAttributes.eventName
					)
				) {
					newrelic.addPageAction(
						`${this.project}_send_data`,
						pageActionAttributes
					);
				}
			}

			this.win[this.scriptObjectName]('dataLayer', data);

			if (event !== undefined) {
				this.win[this.scriptObjectName]('send', event);
			} else {
				this.win[this.scriptObjectName]('send');
			}

			// Send event to BCF event viewer.
			window.dispatchEvent(
				new CustomEvent('eventViewerData', {
					detail: {
						common: {
							project: this.project,
							event: data.events || event || immutabledata.events,
							source: 'ws-tagging',
							payload: {
								...data
							},
							eventHandler: this.eventHandlerName
						}
					}
				})
			);
		};

	/**
	 * Handles the page view event.
	 *
	 * @param {Object} eventData The event data object Relevant properties:
	 * {string} alias The DDC page alias
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.handlePageView =
		function handlePageView(eventData) {
			// custom pageType for feature vehicle pages for Toyota (US687839)
			// this.pageName = this.isFeaturedVehiclePage ? 'Vehicle Listing' : this.mapPageAlias();
			if (this.isFeaturedVehiclePage) {
				this.pageName = 'Vehicle Listing';
			} else {
				this.pageName = this.mapPageAlias();
			}

			const dataLayer = this.buildDataForPageView(eventData.alias);
			// custom pageView for showroom/modal showroom: US917544
			dataLayer.pageType = this.page.isModelShowroomPage()
				? 'Model Showroom'
				: dataLayer.pageType;
			this.sendData(dataLayer, 'pageview');
		};

	/**
	 * Handles the slideshow click event.
	 *
	 * @param {Object} eventData The event data object
	 *
	 * eventData contains the following properties of interest -
	 * position: The position of the slide that was clicked
	 * name: The name of the slide that was clicked
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.handleSlideshowClick =
		function handleSlideshowClick(eventData) {
			this.sendData({
				carouselClick: {
					assetPosition: eventData.position,
					assetName: eventData.name
				},
				events: 'carouselClick'
			});
		};

	/**
	 * Maps the Fuel required by Shift from the Vehicle Details.
	 *
	 * @returns {string} FuelType to send to Shift.
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.mapFuelType =
		function mapFuelType(fuelType) {
			let newFuelType = this.fuelTypeMapping[fuelType];
			if (newFuelType === undefined) {
				newFuelType = this.fuelTypeMapping.DEFAULT;
			}

			return newFuelType;
		};

	/**
	 * Sets the dealerAreaCode code, which is used to distinguish regions for Toyota/Lexus dealers.
	 *
	 * @param {string} dealerAreaCode The DDC dealer code type.
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.setDealerAreaCode =
		function setDealerAreaCode(dealerAreaCode) {
			this.dealerAreaCode = dealerAreaCode;
		};

	/**
	 * Given eventData will return the form displayType
	 *
	 * @param {object} The eventData object from a dataLayer event.
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.formDisplayType = (
		isModal = false
	) => (isModal ? 'modal' : 'in-page');

	/**
	 *
	 * @param {String} fieldName The form field name attribute
	 * @returns A mapping to match the client spec in this story: US971128
	 *
	 * Client said: firstname, lastname, middlename, phonenumber, emailaddress, address, city, state, zipcode, comments, other
	 *
	 * So not specifying whether it is regular form or coapp/email a friend/share etc.
	 */
	tracking.shift.handlers.AbstractEventHandler.prototype.getFormFieldName = (
		fieldName = ''
	) => {
		let formFieldName = 'other';

		if (fieldName) {
			const fieldNameFormatted = fieldName.toLowerCase();

			if (fieldNameFormatted.includes('firstname')) {
				formFieldName = 'firstname';
			} else if (fieldNameFormatted.includes('lastname')) {
				formFieldName = 'lastname';
			} else if (fieldNameFormatted.includes('middlename')) {
				formFieldName = 'middlename';
			} else if (fieldNameFormatted.includes('phone')) {
				formFieldName = 'phonenumber';
			} else if (fieldNameFormatted.includes('email')) {
				formFieldName = 'emailaddress';
			} else if (fieldNameFormatted.includes('street')) {
				formFieldName = 'address';
			} else if (fieldNameFormatted.includes('city')) {
				formFieldName = 'city';
			} else if (fieldNameFormatted.includes('state')) {
				formFieldName = 'state';
			} else if (fieldNameFormatted.includes('postalcode')) {
				formFieldName = 'zipcode';
			} else if (fieldNameFormatted.includes('comments')) {
				formFieldName = 'comments';
			}
		}

		return formFieldName;
	};

	tracking.shift.handlers.AbstractEventHandler.prototype.hasInventoryWidget =
		function hasInventoryWidget() {
			const legacyVLPWidget = this.page.widgetIsPresent(
				/^v9.widgets.inventory-listing.default/
			);
			const legacyVLP =
				win.DDC.dataLayer.page.pageInfo.isVlp && legacyVLPWidget;

			return (
				(win.DDC.dataLayer.page.pageInfo.isVlp && !legacyVLP) ||
				(legacyVLP &&
					![null, ''].includes(
						this.page.dataLayerProperty(
							'page.attributes.vehicleResultCount'
						)
					)) ||
				this.page.widgetIsPresent(/^ws-inv-listing$/) ||
				this.page.currentPageAlias?.()?.includes('NO_RESULTS')
			);
		};

	tracking.shift.handlers.AbstractEventHandler.prototype.getInventoryType =
		function getInventoryType() {
			let inventoryType = this.inventoryTypeMapping.DEFAULT;
			const { vehicles } = win.DDC.dataLayer;

			if (vehicles.length) {
				const hasNew = vehicles.some((car) => car.newOrUsed === 'new');
				const hasUsed = vehicles.some(
					(car) => car.newOrUsed === 'used'
				);
				const hasCertified = vehicles.some(
					(car) => car.certified === 'true' || car.certified === true
				);
				const allCertified = !vehicles.some(
					(car) =>
						car.certified === 'false' || car.certified === false
				);

				if (hasNew && hasUsed) {
					inventoryType = this.inventoryTypeMapping.ALL;
				} else if (hasNew) {
					inventoryType = this.inventoryTypeMapping.NEW;
					// note the next blocks have all vehicles used
				} else if (allCertified) {
					inventoryType = this.inventoryTypeMapping.CERTIFIED;
				} else if (hasCertified) {
					inventoryType = this.inventoryTypeMapping.ALL;
				} else {
					inventoryType = this.inventoryTypeMapping.USED;
				}
			}
			return inventoryType;
		};
};

export default runAbstractEventHandler;
